import {IssueGroupingKey, StoppageCode, usePlantStoppagesDetails} from '@domain/stoppages'
import {isSameDate} from '@hconnect/uikit/src/common'
import {Box, FormControlLabel, Grid, Paper, Switch, Typography} from '@mui/material'
import React, {useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {generatePath} from 'react-router'

import {
  StatusPageHeader,
  DowntimeFilters,
  StoppageDetailsTable,
  TopIssuesByCode
} from '../components'
import {
  DOWNTIME_DETAILS_FOR_MAIN_EQUIPMENT_AND_CODE_FILTER_OPTIONS,
  DowntimeAnalyticsEventName,
  FEATURE_NAMESPACE
} from '../consts'
import {
  useDowntimeFiltersState,
  useTimeRange,
  useTitle,
  useTrackDowntimeDashboardAnalytics
} from '../hooks'
import {useDowntimePlantId} from '../providers'

type EquipmentStoppageDetailsForCodeProps = {
  groupType:
    | IssueGroupingKey.MAIN_EQUIPMENT
    | IssueGroupingKey.EQUIPMENT_NUMBER
    | IssueGroupingKey.STOPPED_MAIN_EQUIPMENT
  groupId: string
  stoppageCode: string
  stoppageConfig?: StoppageCode[]
  downtimeStoppageDetailsBasePath: string
}

export const EquipmentStoppageDetailsForCode: React.FC<EquipmentStoppageDetailsForCodeProps> = ({
  stoppageConfig,
  groupType,
  groupId,
  stoppageCode,
  downtimeStoppageDetailsBasePath
}) => {
  const plantId = useDowntimePlantId()
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const [selectedStoppageCodes, setSelectedStoppageCodes] = useState<string[] | undefined>([
    stoppageCode
  ])
  const [showDescriptions, setShowDescriptions] = useState(false)
  const timeRange = useTimeRange()
  const trackAnalyticsEvent = useTrackDowntimeDashboardAnalytics(plantId)

  const {
    mainEquipments,
    setMainEquipments,
    equipmentNumbers,
    setEquipmentNumbers,
    failureStoppage,
    setFailureStoppage
  } = useDowntimeFiltersState({
    groupType,
    groupId
  })

  const selectedMainEquipments = useMemo(() => mainEquipments?.map((eq) => eq.id), [mainEquipments])
  const selectedEquipments = useMemo(() => equipmentNumbers?.map((eq) => eq.id), [equipmentNumbers])
  const selectedStoppedMainEquipments = useMemo(
    () => (groupType === IssueGroupingKey.STOPPED_MAIN_EQUIPMENT ? [groupId] : undefined),
    [groupType, groupId]
  )

  const {
    data: stoppagesDetails = [],
    isInitialLoading: isStoppageDetailsLoading,
    isError: isStoppageDetailsError,
    refetch: refetchStoppageDetails,
    isRefetching: isStoppageDetailsRefetching
  } = usePlantStoppagesDetails(plantId, {
    ...timeRange,
    selectedStoppedMainEquipments,
    selectedMainEquipments,
    selectedStoppages: failureStoppage,
    selectedEquipments,
    selectedStoppageCodes
  })

  const title = useTitle({
    t,
    groupType,
    groupId,
    stoppageCode,
    stoppagesDetails,
    stoppageConfig
  })

  const timeFramesTitle = useMemo(() => {
    const singleDate = isSameDate(timeRange.from, timeRange.to)
    return timeRange.quickSelectionId
      ? `${t(`quickSelection.${timeRange.quickSelectionId}`)}`
      : `${timeRange.from.toDateString()}${singleDate ? '' : ` - ${timeRange.to.toDateString()}`}`
  }, [timeRange, t])

  return (
    <>
      <StatusPageHeader
        title={title}
        backButtonProps={{
          target: generatePath(downtimeStoppageDetailsBasePath, {
            groupType,
            groupId,
            plantId
          }),
          targetName: t('actions.backToStoppagesDetails')
        }}
        filterComponent={
          <DowntimeFilters
            filteringEventName={DowntimeAnalyticsEventName.UserAdjusts4LevelFilter}
            failureState={{failureStoppage, setFailureStoppage}}
            mainEquipmentState={{mainEquipments, setMainEquipments}}
            stoppageCodesState={{
              stoppageCodes: selectedStoppageCodes,
              setStoppageCodes: setSelectedStoppageCodes
            }}
            equipmentNumbersState={{equipmentNumbers, setEquipmentNumbers}}
            options={DOWNTIME_DETAILS_FOR_MAIN_EQUIPMENT_AND_CODE_FILTER_OPTIONS[groupType]}
          />
        }
      />
      <Grid container spacing={2}>
        <Grid item md={12} xs={12}>
          <TopIssuesByCode
            stoppageConfig={stoppageConfig}
            selectedEquipments={selectedMainEquipments}
            selectedEquipmentNumbers={selectedEquipments}
            selectedStoppages={failureStoppage}
            selectedStoppageCodes={selectedStoppageCodes}
            title={t('label.downtimes')}
            issueCount={10}
          />
        </Grid>
        <Grid item md={12} xs={12}>
          <Paper sx={{padding: 1}}>
            <Box display={'flex'} justifyContent={'space-between'} mb={1}>
              <Box display="flex" m={2} flexDirection="column">
                <Typography variant="subtitle1">{timeFramesTitle}</Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={showDescriptions}
                    data-test-id="events-show-descriptions-toggle"
                    onChange={() => {
                      setShowDescriptions(!showDescriptions)
                      trackAnalyticsEvent(
                        DowntimeAnalyticsEventName.UserAdjustDescriptionColumn,
                        {}
                      )
                    }}
                    color="primary"
                  />
                }
                label={t('actions.toggleDescriptions')}
              />
            </Box>
            <StoppageDetailsTable
              refetch={() => void refetchStoppageDetails()}
              isRefetching={isStoppageDetailsRefetching}
              isLoading={isStoppageDetailsLoading}
              isError={isStoppageDetailsError}
              stoppagesDetails={stoppagesDetails}
              showDescriptions={showDescriptions}
            />
          </Paper>
        </Grid>
      </Grid>
    </>
  )
}
